import { createFileRoute } from "@tanstack/react-router";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { QuantityAdjustedTableColumns } from "@/components/stock-quantity/stock-quantity-meta";
import type { StockQuantity } from "@/store/StockQuantity";
import { ChevronLeft, Loader2 } from "lucide-react";
import DataPlaceholder from "@/components/ui/shared/data-placeholder";
import { useTranslate } from "@/hooks/use-translate";

export const Route = createFileRoute(
  "/(dasboard)/inventory/stock-batchs/$id/quantity-adjusted"
)({
  component: QuantityAdjustedPage,
});

function QuantityAdjustedPage() {
  const store = useAppStore();
  const stock_batch = store.stockBatch.current;
  const params = Route.useParams();
  const { t } = useTranslate();

  const searchForm = useSimpleForm({
    keyword: "",
  });

  const search = ({ page = 1, per_page = 10, stock_batch_id }: any = {}) => {
    store.stockQuantity.fetch({
      page,
      per_page,
      stock_batch_id,
      ...searchForm.values,
    });
  };

  useEffect(() => {
    const id = params.id;
    if (id && store.auth.session) {
      store.stockBatch
        .fetchOne(id)
        .then((stock_batch) => {
          store.stockBatch.setCurrent(stock_batch);
          search({ stock_batch_id: id });
        })
        .catch(store.errors.catch);
    }
  }, [params.id, store.auth.session]);

  if (!stock_batch) {
    return (
      <section>
        <>{store.errors.render()}</>
        <div className="h-24 bg-gray-200 rounded-md flex items-center justify-center">
          {store.loading.status.quantity ? (
            <div>
              <Loader2 className="animate-spin text-primary-400 mx-auto" />
              <p className="text-gray-600">{t("Loading..")}.</p>
            </div>
          ) : (
            <div>
              <DataPlaceholder />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section>
      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>
      <h3 className="font-semibold mb-2"> {t("Quantity Readjusted")} </h3>
      <SearchEngine
        autoComplete={(values: any) => {
          store.stockQuantity.paginate(
            store.stockQuantity.items.filter((item: StockQuantity) =>
              item.stock_batch.batch_code
                ?.toLowerCase()
                .includes(values.keyword)
            )
          );
        }}
        selects={[]}
        form={searchForm}
        onSubmit={undefined}
      />
      <SimpleTable
        cellClass="px-2"
        headCellClass="px-2"
        items={store.stockQuantity.pagination.data}
        columns={QuantityAdjustedTableColumns()}
      />
      <FullPagination
        className="mt-4"
        canEdit={true}
        pagination={store.stockQuantity.pagination}
        onGoto={(page: number) => search({ page })}
        onPerPage={(per_page: any) => search({ per_page })}
      />
    </section>
  );
}
